import { useCallback, useEffect, useRef, useState } from 'react';

import { PrimaryActionButton, SecondaryActionButton, Toast } from '@aztec-realms/ui';

interface PwaRegistrationOptions {
  immediate?: boolean;
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
  onRegisteredSW?: (serviceWorkerUrl: string, registration?: ServiceWorkerRegistration) => void;
  onRegisterError?: (error: unknown) => void;
}

export type PwaRegistrar = (
  options: PwaRegistrationOptions,
) => (reloadPage?: boolean) => Promise<void>;

export function PwaStatus({
  safeToUpdate,
  navigationKey = '',
  presentation = 'game',
  registrar,
}: {
  safeToUpdate: boolean;
  navigationKey?: string;
  presentation?: 'game' | 'account';
  registrar?: PwaRegistrar;
}) {
  const updateServiceWorker = useRef<((reloadPage?: boolean) => Promise<void>) | null>(null);
  const applyingUpdate = useRef(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateError, setUpdateError] = useState<string>();
  const [offlineReady, setOfflineReady] = useState(false);
  // Never read or persist field values. A draft only needs a navigation-scoped latch.
  const [draft, setDraft] = useState({ navigationKey, dirty: false });
  const draftNavigationRef = useRef<string | undefined>(undefined);
  if (draft.navigationKey !== navigationKey) setDraft({ navigationKey, dirty: false });
  const canApply = safeToUpdate && !(draft.navigationKey === navigationKey && draft.dirty);

  useEffect(() => {
    draftNavigationRef.current = undefined;
    const protectDraft = () => {
      draftNavigationRef.current = navigationKey;
      setDraft({ navigationKey, dirty: true });
    };
    document.addEventListener('input', protectDraft, true);
    document.addEventListener('change', protectDraft, true);
    document.addEventListener('submit', protectDraft, true);
    return () => {
      document.removeEventListener('input', protectDraft, true);
      document.removeEventListener('change', protectDraft, true);
      document.removeEventListener('submit', protectDraft, true);
    };
  }, [navigationKey]);

  const applyWaitingUpdate = useCallback(async () => {
    if (
      !canApply ||
      draftNavigationRef.current === navigationKey ||
      document.visibilityState !== 'visible' ||
      !navigator.onLine ||
      applyingUpdate.current ||
      !updateServiceWorker.current
    )
      return;

    applyingUpdate.current = true;
    setUpdateError(undefined);
    try {
      await updateServiceWorker.current(true);
    } catch {
      applyingUpdate.current = false;
      setUpdateError('The update could not be applied yet. Check the connection and retry.');
    }
  }, [canApply, navigationKey]);

  useEffect(() => {
    if (!registrar && (!import.meta.env.PROD || !('serviceWorker' in navigator))) return;

    let disposed = false;
    let updateTimer: number | undefined;
    let registeredWorker: ServiceWorkerRegistration | undefined;
    let checking = false;

    const checkForUpdate = () => {
      if (
        disposed ||
        checking ||
        !registeredWorker ||
        document.visibilityState !== 'visible' ||
        !navigator.onLine
      ) {
        return;
      }
      checking = true;
      void registeredWorker
        .update()
        .catch(() => undefined)
        .finally(() => {
          checking = false;
        });
    };

    const onVisibilityChange = () => checkForUpdate();
    const onOnline = () => checkForUpdate();

    void (async () => {
      const registerSW = registrar ?? (await import('virtual:pwa-register')).registerSW;
      if (disposed) return;

      updateServiceWorker.current = registerSW({
        immediate: true,
        onNeedRefresh: () => {
          if (!disposed) setUpdateAvailable(true);
        },
        onOfflineReady: () => {
          if (!disposed) setOfflineReady(true);
        },
        onRegisterError: () => undefined,
        onRegisteredSW: (_serviceWorkerUrl, registration) => {
          if (disposed || !registration) return;
          registeredWorker = registration;
          document.addEventListener('visibilitychange', onVisibilityChange);
          window.addEventListener('online', onOnline);
          updateTimer = window.setInterval(checkForUpdate, 5 * 60 * 1_000);
          checkForUpdate();
        },
      });
    })().catch(() => undefined);

    return () => {
      disposed = true;
      if (updateTimer !== undefined) window.clearInterval(updateTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, [registrar]);

  useEffect(() => {
    if (!updateAvailable || !canApply) return;
    const applyTimer = window.setTimeout(() => void applyWaitingUpdate(), 0);
    const resume = () => void applyWaitingUpdate();
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      window.clearTimeout(applyTimer);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
    };
  }, [applyWaitingUpdate, canApply, updateAvailable]);

  if (updateAvailable) {
    return (
      <div
        aria-live="polite"
        className={`ar-pwa-toast${presentation === 'account' ? ' ar-pwa-toast--account' : ''}`}
        data-update-state="waiting"
      >
        <Toast
          action={
            updateError && canApply ? (
              <PrimaryActionButton onClick={() => void applyWaitingUpdate()}>
                Retry update
              </PrimaryActionButton>
            ) : undefined
          }
          title={canApply ? 'Applying the realm update' : 'Realm update queued'}
        >
          {updateError ??
            (canApply
              ? 'The latest version will open automatically when you are online.'
              : presentation === 'account'
                ? 'Finish with this form first. Your update is ready for the next screen.'
                : 'Finish what you are doing first. The update will wait until you leave this screen and safely end Journey Mode.')}
        </Toast>
      </div>
    );
  }

  if (offlineReady && presentation !== 'account') {
    return (
      <div className="ar-pwa-toast">
        <Toast
          action={
            <SecondaryActionButton onClick={() => setOfflineReady(false)}>
              Dismiss
            </SecondaryActionButton>
          }
          title="Realm saved for offline viewing"
        >
          Rewards and changes still require a connection.
        </Toast>
      </div>
    );
  }

  return null;
}
